import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";
import { useRegisterCadastrosPageActions } from "../layout";

const emptyForm = {
  solicitante_nome: "",
  solicitante_email: "",
  origem_email: "",
  descricao: "",
  descricao_reduzida: "",
  tipo: "",
  grupo: "",
  justificativa: "",
  justificativa_nao_similar: "",
};

const findOption = (options, codigo) =>
  options.find((opt) => opt.codigo === codigo) || null;

const withOrphanOption = (options, codigo) => {
  if (!codigo || findOption(options, codigo)) return options;
  return [{ codigo, descricao: codigo, label: `${codigo} (não listado)` }, ...options];
};

const NovaSolicitacao = () => {
  const { id } = useParams();
  const editId = id ? Number(id) : null;
  const [searchParams] = useSearchParams();
  const api = useApi();
  const navigate = useNavigate();
  const { notifyError, notifySuccess } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const descricaoInicial = searchParams.get("descricao") || "";

  const [form, setForm] = useState({
    ...emptyForm,
    descricao: descricaoInicial,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(Boolean(editId));
  const [consultaFeita, setConsultaFeita] = useState(false);
  const [candidatos, setCandidatos] = useState([]);
  const [buscandoSb1, setBuscandoSb1] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loadingOpcoes, setLoadingOpcoes] = useState(true);
  const debounceRef = useRef(null);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const tiposOptions = useMemo(() => withOrphanOption(tipos, form.tipo), [tipos, form.tipo]);
  const gruposOptions = useMemo(() => withOrphanOption(grupos, form.grupo), [grupos, form.grupo]);
  const tipoSelecionado = useMemo(() => findOption(tiposOptions, form.tipo), [tiposOptions, form.tipo]);
  const grupoSelecionado = useMemo(
    () => findOption(gruposOptions, form.grupo),
    [gruposOptions, form.grupo]
  );

  const carregarOpcoes = async () => {
    setLoadingOpcoes(true);
    try {
      const { data } = await api.get("Painel/API/Cadastros/Produtos/ListarOpcoes/index.php");
      setTipos(data.data?.tipos || []);
      setGrupos(data.data?.grupos || []);
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao carregar tipos e grupos.");
    } finally {
      setLoadingOpcoes(false);
    }
  };

  const carregar = async () => {
    if (!editId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/Solicitacao/index.php?id=${editId}`
      );
      const sol = data.data;
      if (!["RASCUNHO", "DEVOLVIDO_ESTOQUE"].includes(sol.status)) {
        navigate(`/modulo/cadastros/produtos/${editId}`, { replace: true });
        return;
      }
      setForm({
        solicitante_nome: sol.solicitante_nome || "",
        solicitante_email: sol.solicitante_email || "",
        origem_email: sol.origem_email || "",
        descricao: sol.descricao || "",
        descricao_reduzida: sol.descricao_reduzida || "",
        tipo: sol.tipo || "",
        grupo: sol.grupo || "",
        justificativa: sol.justificativa || "",
        justificativa_nao_similar: sol.justificativa_nao_similar || "",
      });
      setCandidatos(data.data.candidatos || []);
      setConsultaFeita(Boolean(sol.consulta_sb1_em));
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao carregar solicitação.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarOpcoes();
    carregar();
  }, [editId]);

  useRegisterCadastrosPageActions({
    onRefresh: editId ? carregar : undefined,
    refreshing: loading,
    pageTitle: editId ? `Editar #${editId}` : "Nova solicitação",
    pageSubtitle: editId
      ? "Edição de solicitação de produto"
      : "Cadastro de nova solicitação de produto",
  });

  const consultarSb1 = async (termo, { silent = false } = {}) => {
    const q = (termo || form.descricao || "").trim();
    if (q.length < 3) {
      if (!silent) {
        notifyError("Informe ao menos 3 caracteres na descrição para consultar a SB1.");
      }
      return;
    }
    setBuscandoSb1(true);
    try {
      const params = new URLSearchParams({ q, limit: "20" });
      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/ConsultarSB1/index.php?${params.toString()}`
      );
      setCandidatos(data.data || []);
      setConsultaFeita(true);
      if (!silent && (data.data || []).length === 0) {
        notifySuccess("Nenhum produto similar encontrado na SB1.");
      }
    } catch (e) {
      if (!silent) {
        notifyError(e?.response?.data?.message || "Erro ao consultar SB1.");
      }
    } finally {
      setBuscandoSb1(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (form.descricao.trim().length < 3) return undefined;
    debounceRef.current = setTimeout(() => {
      consultarSb1(form.descricao, { silent: true });
    }, 700);
    return () => clearTimeout(debounceRef.current);
  }, [form.descricao]);

  const montarPayload = () => ({
    id: editId || undefined,
    ...form,
    candidatos,
    consulta_sb1_feita: consultaFeita,
  });

  const salvar = async () => {
    if (!form.descricao.trim()) {
      notifyError("Descrição é obrigatória.");
      return null;
    }
    setSaving(true);
    try {
      const { data } = await api.post("Painel/API/Cadastros/Produtos/Salvar/index.php", montarPayload());
      notifySuccess("Solicitação salva.");
      return data.data;
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao salvar.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const salvarRascunho = async () => {
    const sol = await salvar();
    if (sol?.id) navigate(`/modulo/cadastros/produtos/${sol.id}`);
  };

  const enviarEstoque = async () => {
    if (!consultaFeita) {
      notifyError("Consulte a SB1 antes de enviar ao Estoque.");
      return;
    }
    if (candidatos.length > 0 && !form.justificativa_nao_similar.trim()) {
      notifyError("Há candidatos na SB1. Justifique por que o cadastro é novo.");
      return;
    }
    const sol = await salvar();
    if (!sol?.id) return;
    setSaving(true);
    try {
      await api.post("Painel/API/Cadastros/Produtos/EnviarEstoque/index.php", { id: sol.id });
      notifySuccess("Enviado para o Estoque.");
      navigate(`/modulo/cadastros/produtos/${sol.id}`);
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao enviar ao Estoque.");
    } finally {
      setSaving(false);
    }
  };

  const marcarExistente = async (b1Cod) => {
    const sol = await salvar();
    if (!sol?.id) return;
    setSaving(true);
    try {
      await api.post("Painel/API/Cadastros/Produtos/MarcarExistente/index.php", {
        id: sol.id,
        b1_cod_similar: b1Cod,
      });
      notifySuccess(`Produto marcado como já existente: ${b1Cod}`);
      navigate(`/modulo/cadastros/produtos/${sol.id}`);
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao marcar existente.");
    } finally {
      setSaving(false);
    }
  };

  const usarComoReferencia = (item) => {
    if (item.B1_TIPO) setField("tipo", item.B1_TIPO);
    if (item.B1_GRUPO) setField("grupo", item.B1_GRUPO);
    notifySuccess("Tipo e grupo sugeridos a partir do item similar.");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const actionBar = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      sx={{
        width: "100%",
        ...(isMobile
          ? {
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              bgcolor: "background.paper",
              borderTop: "1px solid",
              borderColor: "divider",
              p: 1.5,
              mt: 1,
              mx: { xs: -1.5, md: 0 },
              width: { xs: "calc(100% + 24px)", md: "100%" },
            }
          : {}),
      }}
    >
      <Button
        variant="outlined"
        onClick={() => consultarSb1()}
        disabled={buscandoSb1}
        sx={{ minHeight: 44, flex: { xs: "none", sm: 1 } }}
        fullWidth={isMobile}
      >
        {buscandoSb1 ? "Consultando SB1..." : "Consultar SB1"}
      </Button>
      <Button
        variant="outlined"
        onClick={salvarRascunho}
        disabled={saving}
        sx={{ minHeight: 44, flex: { xs: "none", sm: 1 } }}
        fullWidth={isMobile}
      >
        Salvar rascunho
      </Button>
      <Button
        variant="contained"
        color="secondary"
        onClick={enviarEstoque}
        disabled={saving}
        sx={{ minHeight: 44, flex: { xs: "none", sm: 1 } }}
        fullWidth={isMobile}
      >
        Enviar para Estoque
      </Button>
    </Stack>
  );

  const renderCandidatoCard = (item) => (
    <Paper key={item.B1_COD} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="body2" fontWeight={800}>
        {item.B1_COD}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 1 }}>
        {item.B1_DESC}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mb={1.25}>
        {item.B1_TIPO && <Chip size="small" label={`Tipo ${item.B1_TIPO}`} />}
        {item.B1_GRUPO && <Chip size="small" label={`Grupo ${item.B1_GRUPO}`} />}
        {item.B1_UM && <Chip size="small" label={item.B1_UM} variant="outlined" />}
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => usarComoReferencia(item)}
          disabled={saving}
          sx={{ minHeight: 40, flex: 1 }}
        >
          Usar tipo/grupo
        </Button>
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={() => marcarExistente(item.B1_COD)}
          disabled={saving}
          sx={{ minHeight: 40, flex: 1 }}
        >
          Já existe
        </Button>
      </Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.5 },
        height: "100%",
        overflow: "auto",
        pb: { xs: 0, md: 2.5 },
      }}
    >
      <Typography variant="body2" color="text.secondary" mb={2} sx={{ display: { xs: "none", md: "block" } }}>
        {editId ? `Editar solicitação #${editId}` : "Preencha os dados e consulte a SB1 antes de enviar"}
      </Typography>

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, flex: 1.2, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle2" fontWeight={700}>
              Dados do solicitante
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Nome do solicitante"
                value={form.solicitante_nome}
                onChange={(e) => setField("solicitante_nome", e.target.value)}
                fullWidth
              />
              <TextField
                label="E-mail do solicitante"
                value={form.solicitante_email}
                onChange={(e) => setField("solicitante_email", e.target.value)}
                fullWidth
              />
            </Stack>
            <TextField
              label="Resumo / corpo do e-mail"
              value={form.origem_email}
              onChange={(e) => setField("origem_email", e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Dados do produto
            </Typography>
            <TextField
              label="Descrição *"
              value={form.descricao}
              onChange={(e) => setField("descricao", e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Descrição reduzida"
              value={form.descricao_reduzida}
              onChange={(e) => setField("descricao_reduzida", e.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Autocomplete
                options={tiposOptions}
                value={tipoSelecionado}
                loading={loadingOpcoes}
                onChange={(_, option) => setField("tipo", option?.codigo || "")}
                getOptionLabel={(option) => option?.label || option?.codigo || ""}
                isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo"
                    placeholder="Buscar tipo..."
                    helperText={form.tipo ? `Código: ${form.tipo}` : "Selecione o tipo do Protheus"}
                  />
                )}
                fullWidth
              />
              <Autocomplete
                options={gruposOptions}
                value={grupoSelecionado}
                loading={loadingOpcoes}
                onChange={(_, option) => setField("grupo", option?.codigo || "")}
                getOptionLabel={(option) => option?.label || option?.codigo || ""}
                isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Grupo"
                    placeholder="Buscar grupo..."
                    helperText={form.grupo ? `Código: ${form.grupo}` : "Selecione o grupo do Protheus"}
                  />
                )}
                fullWidth
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Observações
            </Typography>
            <TextField
              label="Justificativa / finalidade"
              value={form.justificativa}
              onChange={(e) => setField("justificativa", e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            {candidatos.length > 0 && (
              <TextField
                label="Justificativa de não similaridade *"
                value={form.justificativa_nao_similar}
                onChange={(e) => setField("justificativa_nao_similar", e.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText="Obrigatório quando há candidatos na SB1 e o cadastro será novo."
              />
            )}

            {!isMobile && actionBar}
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, md: 2 }, flex: 1, borderRadius: 2, minHeight: { xs: "auto", lg: 280 } }}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              Candidatos SB1
            </Typography>
            {!consultaFeita && (
              <Alert severity="info">A consulta à SB1 é obrigatória antes de enviar ao Estoque.</Alert>
            )}
            {consultaFeita && candidatos.length === 0 && (
              <Alert severity="success">Nenhum similar encontrado. Pode seguir com cadastro novo.</Alert>
            )}
            {candidatos.length > 0 && isMobile && (
              <Stack spacing={1}>{candidatos.map(renderCandidatoCard)}</Stack>
            )}
            {candidatos.length > 0 && !isMobile && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Grupo</TableCell>
                      <TableCell>UM</TableCell>
                      <TableCell>NCM</TableCell>
                      <TableCell align="right">Ação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {candidatos.map((item) => (
                      <TableRow key={item.B1_COD}>
                        <TableCell>{item.B1_COD}</TableCell>
                        <TableCell>{item.B1_DESC}</TableCell>
                        <TableCell>{item.B1_TIPO || "—"}</TableCell>
                        <TableCell>{item.B1_GRUPO || "—"}</TableCell>
                        <TableCell>{item.B1_UM}</TableCell>
                        <TableCell>{item.B1_POSIPI}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Button
                              size="small"
                              onClick={() => usarComoReferencia(item)}
                              disabled={saving}
                            >
                              Usar tipo/grupo
                            </Button>
                            <Button
                              size="small"
                              onClick={() => marcarExistente(item.B1_COD)}
                              disabled={saving}
                            >
                              Já existe
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </Paper>
      </Stack>

      {isMobile && actionBar}
    </Box>
  );
};

export default NovaSolicitacao;
